import { useEffect, useState } from "react";
import { Fade, Slide } from "@mui/material";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectActiveRobotId, selectActiveRobotVersion } from "../../store/ses/sesRobotSlice";
import {
	addScript,
	changeScriptDataInList,
	clearTestingScript,
	deleteScript,
	editScript,
	getScript,
	getScriptList,
	selectAddingScript,
	selectDeletingScript,
	selectEditingScript,
	selectScript,
	selectScriptList,
	setTabToggle,
} from "../../store/ses/sesScriptSlice";
import useUncommittedRobotsList from "../../hooks/useUncommittedRobotsList";
import useAccessRight from "../../hooks/useAccessRight";
import useTranslate from "../../hooks/useTranslate";
import { SES } from "../../constants/accessRights";
import { SCRIPT_TYPES_LIST } from "../../constants/robotConfigLists";
import { ScriptType } from "../../types/ses/sesScriptTypes";
import { RequestStatus, ResponseStatus } from "../../types/statusTypes";
import { Hint } from "@/HOC";
import Script from "./Script/Script";
import { AlertDialog, CustomButton } from "../../ui";
import ScriptTest from "./ScriptTest/ScriptTest";
import BlockWithLoading from "../../ui/BlockWithLoading/BlockWithLoading";
import BlockWithError from "../../ui/BlockWithError/BlockWithError";
import BlockWithInfo from "../../ui/BlockWithInfo/BlockWithInfo";
import { IChangeFlg, IScriptsProps } from "./Scripts.props";
import styles from "./Scripts.module.scss";
import { selectSesHints } from "@/store/hintsSlice";
import { CustomMenuItem, CustomTextField } from "@/ui";

const Scripts = ({ showScripts, setShowScripts }: IScriptsProps): JSX.Element => {
	const [type, setType] = useState<ScriptType>("endpoint"); // тип скрипта
	const [selectScriptId, setSelectScriptId] = useState<string>(""); // выбранный id скрипта
	const [name, setName] = useState<string>(""); // имя скрипта
	const [code, setCode] = useState<string>(""); // код скрипта
	const [changeFlg, setChangeFlg] = useState<IChangeFlg>({ thisIs: false, listOfChanges: [] }); // флаг, уведомляющий об изменении данных и возможности сохранить эти изменения
	const [showAlertDialogDelete, setShowAlertDialogDelete] = useState<boolean>(false); // показ диалогового окна для удаления скрипта

	const [showTestScriptBlock, setShowTestScriptBlock] = useState<boolean>(false); // показ блока тестирования скрипта

	const dispatch = useAppDispatch();
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const scriptList = useAppSelector(selectScriptList); // store - список скриптов
	const script = useAppSelector(selectScript); // store - скрипт

	const addingScript = useAppSelector(selectAddingScript); // store - статус добавления скрипта
	const editingScript = useAppSelector(selectEditingScript); // store - статус изменения скрипта
	const deletingScript = useAppSelector(selectDeletingScript); // store - статус удаления скрипта

	const sesHints = useAppSelector(selectSesHints);

	const uncommittedRobotsList = useUncommittedRobotsList(); // hook для обработки списка незакоммиченных настроек роботов
	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за id робота
	useEffect(() => {
		setSelectScriptId(""); // очищаем id скрипта
		setShowTestScriptBlock(false); // закрываем блок проверки
		dispatch(clearTestingScript()); // очистка данных теста скрипта
	}, [activeRobotId]);

	// следим за открытие вкладки из store
	useEffect(() => {
		if (scriptList.openTabWithData.isOpen) {
			setType(scriptList.openTabWithData.scriptType); // вписываем тип скрипта
			const isExisting = scriptList.data.some((script) => script.id === scriptList.openTabWithData.scriptId);
			setSelectScriptId(isExisting ? scriptList.openTabWithData.scriptId : ""); // вписывыаем id скрипта
		}
	}, [scriptList.openTabWithData.isOpen]);

	// следим за списком скриптов и типом
	useEffect(() => {
		// если есть данные (вне режима открытия из действия) - вписываем в поле id из списка, иначе очищаем
		if (scriptList.data.length > 0) {
			if (!scriptList.openTabWithData.isOpen) setSelectScriptId(scriptList.data.filter((scriptItem) => scriptItem.type === type)[0]?.id || "");
		}
	}, [scriptList.data, type]);

	// следим за выбранным скриптом и открытием вкладки
	useEffect(() => {
		setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
		if (selectScriptId !== "" && activeRobotId && showScripts) {
			dispatch(getScript({ robotId: activeRobotId, scriptId: selectScriptId })); // получаем скрипт
		}
	}, [selectScriptId, showScripts]);

	// следим за статусом добавления скрипта
	useEffect(() => {
		// если успешно
		if (addingScript.status === RequestStatus.IDLE && addingScript.error === ResponseStatus.SUCCESS && addingScript.message !== "") {
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			addingScript.id && setSelectScriptId(addingScript.id); // вписываем id нового скрипта
			isAccess(SES.SCRIPT_LIST) && activeRobotId && dispatch(getScriptList(activeRobotId)); // обновляем список скриптов
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [addingScript]);

	// следим за статусом изменения скрипта
	useEffect(() => {
		// если успешно
		if (editingScript.status === RequestStatus.IDLE && editingScript.error === ResponseStatus.SUCCESS && editingScript.message !== "") {
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			script.name !== name && dispatch(changeScriptDataInList({ scriptId: selectScriptId, name })); // если изменилось имя - меняем в списке скриптов
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [editingScript]);

	// следим за статусом удаления скрипта
	useEffect(() => {
		// если успешно
		if (deletingScript.status === RequestStatus.IDLE && deletingScript.error === ResponseStatus.SUCCESS && deletingScript.message !== "") {
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			setSelectScriptId(""); // сбрасываем выбор скрипта
			isAccess(SES.SCRIPT_LIST) && activeRobotId && dispatch(getScriptList(activeRobotId)); // обновляем список скриптов
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [deletingScript]);

	// обработчик добавления скрипта
	const addScriptHandler = (): void => {
		if (activeRobotId) {
			const newName =
				type === "endpoint" || type === "data"
					? `${translate("title_newScript")} ${scriptList.data.filter((scriptItem) => scriptItem.type === type).length + 1}`
					: `${translate("title_script")} ${type}`; // создаем новое имя для определенных типов
			const scriptId = type === "endpoint" || type === "data" ? undefined : type; // id скрипта для определенных типов

			const getDefaultScript = (type: ScriptType): string => {
				switch (type) {
					case "endpoint":
						return translate("_defaultScriptEndpoint");
					case "data":
						return translate("_defaultScriptData");
					case "start":
						return translate("_defaultScriptStart");
					case "beforeask":
						return translate("_defaultScriptBeforeask");
					case "afterask":
						return translate("_defaultScriptAfterask");
					case "transfer":
						return translate("_defaultScriptTransfer");
					case "hangup":
						return translate("_defaultScriptHangup");
					default:
						const _: never = type;
						return _;
				}
			};

			dispatch(addScript({ robotId: activeRobotId, name: newName, type, scriptId, code: getDefaultScript(type) })); // добавляем новый скрипт
		}
	};

	// обработчик сохранения скрипта
	const editScriptHandler = (): void => {
		if (activeRobotId) {
			dispatch(
				editScript({
					robotId: activeRobotId,
					scriptId: selectScriptId,
					name: changeFlg.listOfChanges.includes("name") ? name : undefined,
					code: changeFlg.listOfChanges.includes("code") ? code : undefined,
				})
			); // сохраняем изменения
		}
	};

	// обработчик удаления скрипта
	const deleteScriptHandler = (): void => {
		if (activeRobotId) {
			dispatch(deleteScript({ robotId: activeRobotId, scriptId: selectScriptId })); // удаляем скрипт
		}
	};

	// обработчик закрытия вкладки
	const closeHandler = (): void => {
		setShowScripts();
		scriptList.openTabWithData.isOpen && dispatch(setTabToggle(false)); // дублируем закрытие вкладки в store
	};

	return (
		<Slide direction="left" in={showScripts} mountOnEnter unmountOnExit>
			<div className={styles.modal} onMouseDown={closeHandler}>
				<div
					className={cn(styles.container, {
						[styles.containerFullWidth]: showTestScriptBlock,
					})}
					onMouseDown={(e) => e.stopPropagation()}
				>
					{/* загрузка списка скриптов */}
					{scriptList.status === RequestStatus.LOADING && <BlockWithLoading />}

					{/* ошибка получения списка скриптов */}
					{scriptList.status === RequestStatus.FAILED && (
						<BlockWithError message={scriptList.message} updateHandler={() => activeRobotId && dispatch(getScriptList(activeRobotId))} />
					)}

					{/* скрипты */}
					{scriptList.status === RequestStatus.IDLE && (
						<Fade in={true} timeout={500}>
							<div className={styles.scripts}>
								{isAccess(SES.SCRIPT_TEST) && (
									<div
										className={cn(styles.scriptsTestingWrapper, {
											[styles.scriptsTestingWrapperShow]: showTestScriptBlock,
										})}
									>
										{/* тест скрипта */}
										<ScriptTest scriptOrDataElemId={selectScriptId} changeFlg={changeFlg.thisIs} useOfValue="notUsed" />
										{showTestScriptBlock && <div className={styles.separator}></div>}
									</div>
								)}

								<div className={styles.scriptsWrapper}>
									<div className={styles.scriptsTop}>
										<div className={styles.block}>
											{/* тип скрипта (область действия) */}
											{!scriptList.openTabWithData.isOpen && (
												<CustomTextField
													select
													margin="dense"
													label={translate("select_scopeOfAction")}
													value={type}
													onChange={(e) => setType(e.target.value as ScriptType)}
													style={{ flexShrink: 0 }}
													hint={sesHints.hint_sesFieldScriptScope}
												>
													{SCRIPT_TYPES_LIST.map(({ type, translation }) => {
														return (
															<CustomMenuItem
																key={type}
																value={type}
																hint={translate(`hint_sesScript${type.charAt(0).toUpperCase() + type.slice(1)}`)}
																hintPosition="left"
															>
																{translate(translation)}{" "}
																{scriptList.data.filter((scriptItem) => scriptItem.type === type).length > 0 &&
																	`(${scriptList.data.filter((scriptItem) => scriptItem.type === type).length})`}
															</CustomMenuItem>
														);
													})}
												</CustomTextField>
											)}
											{/* выбор скрипта */}
											{(type === "endpoint" || type === "data") && scriptList.data.filter((scriptItem) => scriptItem.type === type).length > 0 && (
												<CustomTextField
													select
													fullWidth
													margin="dense"
													label={translate("input_script")}
													disabled={!isAccess(SES.SCRIPT_GET)}
													value={selectScriptId}
													onChange={(e) => e.target.value && setSelectScriptId(e.target.value)}
													hint={sesHints.hint_sesBlockScriptsList}
												>
													{scriptList.data
														.filter((scriptItem) => scriptItem.type === type)
														.map(({ id, name }) => (
															<CustomMenuItem key={id} value={id}>
																{name}
															</CustomMenuItem>
														))}
												</CustomTextField>
											)}
											{activeRobotVersion === "draft" && (
												<div className={cn(styles.block, styles.blockButtons)}>
													{/* добавление скрипта */}
													{isAccess(SES.SCRIPT_ADD) && (
														<CustomButton
															fullWidth
															disabled={scriptList.data.filter((scriptItem) => scriptItem.type === type && type !== "endpoint" && type !== "data").length > 0}
															onClick={addScriptHandler}
															hint={sesHints.hint_sesButtonAddScript}
															hintPosition="bottom"
														>
															{translate("button_add")}
														</CustomButton>
													)}
													{/* сохранение скрипта */}
													{isAccess(SES.SCRIPT_EDIT) && (
														<CustomButton
															fullWidth
															disabled={!changeFlg.thisIs}
															onClick={editScriptHandler}
															hint={sesHints.hint_sesButtonSaveScript}
															hintPosition="bottom"
														>
															{translate("button_save")}
														</CustomButton>
													)}
													{/* удаление скрипта */}
													{isAccess(SES.SCRIPT_DELETE) && (
														<CustomButton
															fullWidth
															disabled={scriptList.data.length === 0 || selectScriptId === "" || script.status === RequestStatus.LOADING}
															onClick={() => setShowAlertDialogDelete(true)}
															color="error"
															hint={sesHints.hint_sesButtonDeleteScript}
															hintPosition="bottom"
														>
															{translate("button_delete")}
														</CustomButton>
													)}
												</div>
											)}
										</div>

										{/* скрипт */}
										{scriptList.data.filter((scriptItem) => scriptItem.type === type).length > 0 ? (
											<>
												{selectScriptId === "" ? (
													<BlockWithInfo title="title_notFound" />
												) : (
													<Script
														changeFlg={changeFlg}
														setChangeFlg={setChangeFlg}
														selectScriptId={selectScriptId}
														name={name}
														setName={setName}
														code={code}
														setCode={setCode}
													/>
												)}
											</>
										) : (
											<BlockWithInfo title="title_emptyList" />
										)}
									</div>
								</div>
							</div>
						</Fade>
					)}

					{/* подтверждение удаления скрипта */}
					<AlertDialog
						showAlertDialog={showAlertDialogDelete}
						setShowAlertDialog={setShowAlertDialogDelete}
						submitHandler={deleteScriptHandler}
						title="dialog_deleteScript"
						description="dialog_deleteScriptConfirm"
					/>

					{isAccess(SES.SCRIPT_TEST) && (
						<div className={styles.tagCheck} onClick={() => setShowTestScriptBlock((prev) => !prev)}>
							<Hint hint={sesHints.hint_sesBlockScriptDebug} position="left" offset={[0, -6]}>
								<>{translate("button_examination")}</>
							</Hint>
						</div>
					)}
					<div className={styles.tagClose} onClick={closeHandler}>
						{translate("button_close")}
					</div>
				</div>
			</div>
		</Slide>
	);
};

export default Scripts;
